import React, { useEffect, useState } from 'react';
import cn from 'classnames';

import Button from 'components/form/Button.component';
import TypedOption from 'components/form/TypedOption.component';
import DropdownFoldout from 'components/generic/DropdownFoldout.component';
import Icon from 'components/generic/Icon.component';
import STAFF_NAMES from 'constants/settings/staff.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import C from 'utils/values/constant.util';

import SpeedFeedback from 'settings/user/SpeedFeedback.component';
import SuccessfulText from 'settings/user/SuccessfulText.component';
import WarningText from 'settings/user/WarningText.component';

import CN from './NetworkSpeedChecker.module.scss';

const WEAK_SPEED = 15000;

const SPEED = C({
  weak: '0 - 15,000',
  slow: '15,000 - 25,000',
  medium: '25,000 - 40,000',
  fast: '40,000 - 60,000', // my speed is here: 45596
  faster: '60,000+',
});

const SPEED_INFO = C({
  [SPEED.weak]: { speed: 10000 },
  [SPEED.slow]: { speed: 20000 },
  [SPEED.medium]: { speed: 32000 },
  [SPEED.fast]: { speed: 50000 },
  [SPEED.faster]: { speed: 70000 },
});

const SPEED_THRESHOLDS = {
  [STAFF_NAMES.staff_1_3]: 15000,
  [STAFF_NAMES.staff_4_6]: 25000,
  [STAFF_NAMES.staff_7_10]: 40000,
  [STAFF_NAMES.staff_11_plus]: 60000,
};

const DeviceChecker = ({ selectedOption, onRunTest }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const { val: isDropdownRendered, nay: closeDropdown, tip: toggleDropdown } = useTip(false);
  const [speedRange, setSpeedRange] = useState('');

  const speedInfo = SPEED_INFO[speedRange];

  const getSpeedMessage = () => {
    if (!speedInfo) return null;
    const speed = parseFloat(speedInfo.speed);

    const threshold = SPEED_THRESHOLDS[selectedOption];

    if (speed >= threshold) {
      return <SuccessfulText />;
    }

    return <WarningText />;
  };

  const handleDropdownSelect = (range) => {
    setSpeedRange(range);
    closeDropdown();
  };

  useEffect(() => {
    setSpeedRange('');
  }, [selectedOption]);

  return (
    <div
      data-bem="DeviceChecker"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.titleContainer}>
        <div className={CN.title}>
          <div className={CN.iconContainer}>
            <Icon variant="deviceTablet" width="xl" />
          </div>
          {t('staffAndBusiness.userNetwork.hardwareInformation.section2Title')}
        </div>
        <div className={CN.runButtonContainer}>
          <Button onClick={onRunTest} variant="primary" xSpace>
            {t('staffAndBusiness.userNetwork.hardwareInformation.runTestButton')}
          </Button>
        </div>
      </div>
      <div className={CN.text}>{t('staffAndBusiness.userNetwork.hardwareInformation.section2Description')}</div>

      <div>
        <div className={CN.resultContainer}>
          <div className={CN.speedInfoContainer}>
            <div className={CN.label}>{t('staffAndBusiness.userNetwork.hardwareInformation.chromiumScore')}</div>
            <div className={CN.dropdownContainer}>
              <div
                onClick={toggleDropdown}
                className={cn({
                  [CN.dropdownButton]: true,
                  [CN.active]: isDropdownRendered,
                })}
              >
                <div> {speedRange || t('staffAndBusiness.userNetwork.hardwareInformation.selectPeedRange')}</div>
                <Icon variant="arrowTriangle" width="xs" />
              </div>

              {isDropdownRendered && (
                <DropdownFoldout width="full" position="bottom">
                  <DropdownFoldout.Body>
                    <div className={CN.dropdownContent}>
                      <div className={CN.item}>
                        <TypedOption type="simple-checkbox" checked={speedRange === SPEED.weak} onCheck={() => handleDropdownSelect(SPEED.weak)}>
                          <div className={CN.name}>{SPEED.weak}</div>
                        </TypedOption>
                      </div>
                      <div className={CN.item}>
                        <TypedOption type="simple-checkbox" checked={speedRange === SPEED.slow} onCheck={() => handleDropdownSelect(SPEED.slow)}>
                          <div className={CN.name}>{SPEED.slow}</div>
                        </TypedOption>
                      </div>
                      <div className={CN.item}>
                        <TypedOption type="simple-checkbox" checked={speedRange === SPEED.medium} onCheck={() => handleDropdownSelect(SPEED.medium)}>
                          <div className={CN.name}>{SPEED.medium}</div>
                        </TypedOption>
                      </div>
                      <div className={CN.item}>
                        <TypedOption type="simple-checkbox" checked={speedRange === SPEED.fast} onCheck={() => handleDropdownSelect(SPEED.fast)}>
                          <div className={CN.name}>{SPEED.fast}</div>
                        </TypedOption>
                      </div>
                      <div className={CN.item}>
                        <TypedOption type="simple-checkbox" checked={speedRange === SPEED.faster} onCheck={() => handleDropdownSelect(SPEED.faster)}>
                          <div className={CN.name}>{SPEED.faster}</div>
                        </TypedOption>
                      </div>
                    </div>
                  </DropdownFoldout.Body>
                </DropdownFoldout>
              )}
            </div>
          </div>

          <div className={CN.speedResultContainer}>
            <div className={CN.label}>{t('staffAndBusiness.userNetwork.hardwareInformation.analysisSpeed')}</div>
            {speedInfo ? getSpeedMessage() : <div className={CN.message}>{t('staffAndBusiness.userNetwork.hardwareInformation.analysisInputPlaceholder')}</div>}
          </div>
        </div>
        <div className={CN.titleContainer}>
          <div className={CN.subTitle}>{t('staffAndBusiness.userNetwork.hardwareInformation.nextSteps')}</div>
        </div>

        {speedInfo && <SpeedFeedback speed={speedInfo.speed} threshold={SPEED_THRESHOLDS[selectedOption] || WEAK_SPEED} />}
      </div>
    </div>
  );
};

export default DeviceChecker;
